
import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { VanityAPI } from '../services/api';
import { VanityPage, FooterConfig } from '../types';

const Header: React.FC = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [navLinks, setNavLinks] = useState<Partial<VanityPage>[]>([]);
  const [footerConfig, setFooterConfig] = useState<FooterConfig | null>(null);
  const location = useLocation();

  useEffect(() => {
    const fetchNav = async () => {
      try {
        const [links, config] = await Promise.all([
          VanityAPI.getNavLinks(),
          VanityAPI.getFooterConfig()
        ]);
        setNavLinks(links);
        setFooterConfig(config);
      } catch (err) {
        console.error("Failed to load nav links:", err);
      }
    };
    fetchNav();
  }, []);

  const isActive = (path: string) => location.pathname === path;

  // Split links into main (menu) and extra (dropdown/drawer)
  const menuLinks = navLinks.filter(l => l.showInMenu);

  return (
    <>
      <header className="fixed top-0 left-0 w-full z-50 bg-vanity-white/95 backdrop-blur-md border-b border-vanity-charcoal/5">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 h-20 md:h-24 flex items-center justify-between">
          {/* Logo */}
          <Link
            to="/"
            className="flex flex-col items-center group transition-transform hover:scale-[1.02] duration-500"
          >
            <span className="font-serif text-2xl lg:text-4xl text-vanity-pink tracking-[0.2em] font-bold leading-none select-none">
              VANITY
            </span>
            <span className="hidden lg:block font-sans text-[6px] lg:text-[8px] uppercase tracking-[0.6em] text-vanity-black mt-1 font-bold group-hover:text-vanity-pink transition-colors">
              Beauty &middot; Health &middot; Lifestyle
            </span>
          </Link>

          {/* Desktop/Tablet Main Navigation */}
          <nav className="hidden md:flex items-center space-x-12">
            {/* Hardcoded First Link: AKTUELL */}
            <Link
              to="/latest"
              className={`font-sans text-[11px] uppercase tracking-[0.3em] font-extrabold hover:text-vanity-pink ${isActive('/latest') ? 'text-vanity-pink border-b-2 border-vanity-pink pb-1' : 'text-vanity-black'}`}
            >
              Aktuelle Ausgabe
            </Link>
            {menuLinks.map((link) => (
              <Link
                key={link.id}
                to={`/${link.slug === 'home' ? '' : link.slug}`}
                className={`font-sans text-[11px] uppercase tracking-[0.3em] transition-all duration-300 font-extrabold hover:text-vanity-pink ${isActive(`/${link.slug}`) ? 'text-vanity-pink border-b-2 border-vanity-pink pb-1' : 'text-vanity-black'
                  }`}
              >
                {link.name}
              </Link>
            ))}

          </nav>

          {/* Persistent Hamburger Menu */}
          <button
            onClick={() => setIsDrawerOpen(true)}
            className="flex flex-col space-y-1.5 p-3 group transition-all min-w-[48px] min-h-[48px] items-center justify-center"
            aria-label="Navigationsmenü öffnen"
          >
            <span className="w-10 h-[2px] bg-vanity-black group-hover:bg-vanity-pink transition-all duration-300"></span>
            <span className="w-10 h-[2px] bg-vanity-black group-hover:bg-vanity-pink transition-all duration-300"></span>
            <span className="w-6 h-[2px] bg-vanity-black group-hover:bg-vanity-pink transition-all duration-300 self-end"></span>
          </button>
        </div>
      </header>

      {/* Drawer Backdrop */}
      <div
        className={`fixed inset-0 z-[60] bg-vanity-black/40 backdrop-blur-sm transition-opacity duration-700 ${isDrawerOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        onClick={() => setIsDrawerOpen(false)}
      ></div>

      {/* Side Drawer */}
      <div className={`fixed top-0 right-0 h-screen w-full md:w-[520px] bg-vanity-white z-[70] shadow-2xl transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isDrawerOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full p-12 md:p-20 overflow-y-auto">
          {/* Top Bar in Drawer */}
          <div className="flex justify-between items-center mb-10">
            <span className="font-sans text-[10px] uppercase tracking-[0.5em] text-vanity-pink font-extrabold italic">Perspective</span>
            <button
              onClick={() => setIsDrawerOpen(false)}
              className="text-vanity-black hover:text-vanity-pink transition-all duration-300 group flex items-center gap-4"
            >
              <span className="font-sans text-[10px] uppercase tracking-[0.3em] font-extrabold">Dismiss</span>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                <path d="M18 6L6 18M6 6l12 12" />
              </svg>
            </button>
          </div>

          <nav className="flex-grow">
            <div className="space-y-6">
              <span className="font-sans text-[9px] uppercase tracking-[0.8em] text-vanity-grey mb-4 block font-bold">Menu Übersicht</span>
              <div className="flex flex-col space-y-2">
                {/* Hardcoded First Link: AKTUELL */}
                <Link
                  to="/latest"
                  onClick={() => setIsDrawerOpen(false)}
                  className="group block text-left py-3 min-h-[48px] flex items-center"
                >
                  <span className={`font-serif text-lg md:text-xl transition-all duration-500 italic font-medium relative ${isActive('/latest') ? 'text-vanity-pink border-b-2 border-vanity-pink' : 'text-vanity-black hover:text-vanity-pink'}`}>
                    Aktuelle Ausgabe
                    <span className={`absolute -left-6 top-1/2 -translate-y-1/2 w-4 h-px bg-vanity-pink transition-all duration-500 ${isActive('/latest') ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}></span>
                  </span>
                </Link>
                {navLinks.filter(l => l.showInDropdown).map((link) => (
                  <Link
                    key={link.id}
                    to={`/${link.slug === 'home' ? '' : link.slug}`}
                    onClick={() => setIsDrawerOpen(false)}
                    className="group block text-left py-3 min-h-[48px] flex items-center"
                  >
                    <span className={`font-serif text-lg md:text-xl transition-all duration-500 italic font-medium relative ${isActive(`/${link.slug === 'home' ? '' : link.slug}`) ? 'text-vanity-pink border-b-2 border-vanity-pink' : 'text-vanity-black hover:text-vanity-pink'}`}>
                      {link.name}
                      <span className={`absolute -left-6 top-1/2 -translate-y-1/2 w-4 h-px bg-vanity-pink transition-all duration-500 ${isActive(`/${link.slug === 'home' ? '' : link.slug}`) ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`}></span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          </nav>

          {/* Social Icons in Drawer */}
          <div className="mt-20 pt-12 border-t border-vanity-charcoal/10">
            <h4 className="font-sans text-[11px] uppercase tracking-[0.5em] text-vanity-black mb-8 font-extrabold">Connect &middot; Discover</h4>
            <div className="flex space-x-10 mb-12">
              {footerConfig?.instagram && (
                <a href={footerConfig.instagram} target="_blank" rel="noreferrer" aria-label="Besuchen Sie uns auf Instagram" className="text-vanity-black hover:text-vanity-pink transition-all duration-300 hover:scale-125 hover:rotate-6 p-3 -m-3 inline-flex items-center justify-center min-w-[48px] min-h-[48px]">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                    <path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"></path>
                    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                  </svg>
                </a>
              )}
              {footerConfig?.facebook && (
                <a href={footerConfig.facebook} target="_blank" rel="noreferrer" aria-label="Besuchen Sie uns auf Facebook" className="text-vanity-black hover:text-vanity-pink transition-all duration-300 hover:scale-125 hover:rotate-6 p-3 -m-3 inline-flex items-center justify-center min-w-[48px] min-h-[48px]">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"></path>
                  </svg>
                </a>
              )}
              {footerConfig?.linkedin && (
                <a href={footerConfig.linkedin} target="_blank" rel="noreferrer" aria-label="Besuchen Sie uns auf LinkedIn" className="text-vanity-black hover:text-vanity-pink transition-all duration-300 hover:scale-125 hover:rotate-6 p-3 -m-3 inline-flex items-center justify-center min-w-[48px] min-h-[48px]">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2">
                    <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                    <rect x="2" y="9" width="4" height="12"></rect>
                    <circle cx="4" cy="4" r="2"></circle>
                  </svg>
                </a>
              )}
            </div>

            <div className="mt-12">
              <div className="space-y-1">
                <p className="font-sans text-[10px] uppercase tracking-[0.4em] text-vanity-black font-extrabold">VANITY Magazin</p>
                <p className="font-sans text-[8px] uppercase tracking-[0.3em] text-vanity-grey">
                  Refining Aesthetic &middot; Medical Dialogue.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Header;
